/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import React, { useCallback, useEffect, useMemo } from "react";
import { SSOAuthButton as SSOButton } from "./components/SSOAuthButton";
import { useGoogleLogin } from "@react-oauth/google";
import { useAppleAuth } from "@/hooks/auth/useAppleAuth";
import { useMicrosoftAuth } from "@/hooks/auth/useMicrosoftAuth";
import { useSSOState, clearSSOToasts } from "@/components/SSOAuthButton/hooks/useSSOState";
import { useSSOAuth } from "@/components/SSOAuthButton/hooks/useSSOAuth";
import { useToast } from "@/hooks/ui/useToast";
import { useLogin } from "@/hooks/auth/useLogin";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";

import { SSO_PROVIDER, SsoProviderId, type SSOAuthContainerProps } from "./types";

export const SSOAuthContainer = ({
  mode,
  providers = [SSO_PROVIDER.GOOGLE, SSO_PROVIDER.MICROSOFT, SSO_PROVIDER.APPLE],
  rounded,
  isFormLoading,
  labels,
  onSsoCompleted,
  onSsoLoadingChange,
  errors,
}: SSOAuthContainerProps) => {
  const { toast } = useToast();
  const { onSaveCookie } = useLogin();

  const {
    setSsoLoading,
    isSsoLoading,
    activeProvider,
    accessToken,
    setAccessToken,
    resetAllAuthState,
  } = useSSOState();

  // SSO Auth Logic according to activeProvider
  // Only initialize when we have an active provider
  const ssoLogic = useSSOAuth({
    provider: activeProvider,
    onSaveCookie,
    onSsoCompleted,
    accessToken,
    errors,
    setSsoLoading,
  });

  // Reset centralizado al cambiar de modo (SignIn/SignUp)
  useEffect(() => {
    resetAllAuthState([ssoLogic]);
  }, [mode, resetAllAuthState]);

  // Loading state sync
  const isApiLoading = ssoLogic.isLoading;
  useEffect(() => {
    onSsoLoadingChange?.(isSsoLoading || isApiLoading);
  }, [isSsoLoading, isApiLoading, onSsoLoadingChange]);

  // Auth action executor that captures the provider at click time
  const executeAuthActionRef = React.useRef<(provider: SsoProviderId, token: string) => void>();

  const executeAuthAction = useCallback(
    (provider: SsoProviderId, token: string) => {
      // Only proceed if we have a valid provider
      if (!provider) {
        return;
      }
      if (mode === "signin") {
        ssoLogic.handleLogin(token);
      } else {
        ssoLogic.handleRegister(token);
      }
    },
    [mode, ssoLogic]
  );

  useEffect(() => {
    executeAuthActionRef.current = executeAuthAction;
  }, [executeAuthAction]);

  // --- GOOGLE LOGIC ---
  const runGoogleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: (response) => {
      setAccessToken(response.access_token);
      executeAuthActionRef.current?.(SSO_PROVIDER.GOOGLE, response.access_token);
    },
    onError: (error) => {
      const message = ApiErrorMessageBuilder.buildToastMessage(error, {
        fallbackMessage: API_ERROR_FALLBACKS.AUTH.GOOGLE,
      });
      toast({ message, type: "error" });
      setSsoLoading(false, null);
    },
    onNonOAuthError: (event) => {
      if (["popup_closed", "unknown"].includes(event.type)) {
        setSsoLoading(false, null);
      }
    },
  });

  const handleGoogleClick = useCallback(() => {
    clearSSOToasts();
    setSsoLoading(true, SSO_PROVIDER.GOOGLE);
    runGoogleLogin();
  }, [runGoogleLogin, setSsoLoading]);

  // --- APPLE LOGIC ---
  const runAppleLogin = useAppleAuth({
    onSuccess: (response) => {
      const idToken = response?.authorization?.id_token;
      if (idToken) {
        setAccessToken(idToken);
        executeAuthActionRef.current?.(SSO_PROVIDER.APPLE, idToken);
      } else {
        setSsoLoading(false, null);
      }
    },
    onError: () => {
      setSsoLoading(false, null);
    },
  });

  const handleAppleClick = useCallback(() => {
    clearSSOToasts();
    setSsoLoading(true, SSO_PROVIDER.APPLE);
    runAppleLogin.signIn();
  }, [runAppleLogin, setSsoLoading]);

  // --- MICROSOFT LOGIC ---
  const runMicrosoftLogin = useMicrosoftAuth({
    onSuccess: (idToken) => {
      if (idToken) {
        setAccessToken(idToken);
        executeAuthActionRef.current?.(SSO_PROVIDER.MICROSOFT, idToken);
      } else {
        setSsoLoading(false, null);
      }
    },
    onError: () => {
      setSsoLoading(false, null);
    },
  });

  const handleMicrosoftClick = useCallback(() => {
    clearSSOToasts();
    setSsoLoading(true, SSO_PROVIDER.MICROSOFT);
    runMicrosoftLogin.signIn();
  }, [runMicrosoftLogin, setSsoLoading]);

  const handlers = useMemo(
    () => ({
      [SSO_PROVIDER.GOOGLE]: handleGoogleClick,
      [SSO_PROVIDER.APPLE]: handleAppleClick,
      [SSO_PROVIDER.MICROSOFT]: handleMicrosoftClick,
    }),
    [handleGoogleClick, handleAppleClick, handleMicrosoftClick]
  );

  // Render Helpers
  const isSsoLocked = isSsoLoading || isApiLoading;

  const getProviderLabels = useCallback(
    (provider: SsoProviderId) => {
      const providerLabelData = labels[provider];

      return {
        label: providerLabelData?.label || "Unknown",
        loadingLabel: providerLabelData?.loading || "Loading",
      };
    },
    [labels]
  );

  const renderedButtons = useMemo(() => {
    return providers.map((provider) => {
      const isActive = activeProvider === provider;
      const { label, loadingLabel } = getProviderLabels(provider);

      return (
        <SSOButton
          key={provider}
          provider={provider}
          label={label}
          loadingLabel={loadingLabel}
          isShowingLoadingLabel={isSsoLoading && isActive}
          isSsoLocked={isSsoLocked}
          isFormLoading={isFormLoading}
          rounded={rounded}
          onClick={handlers[provider]}
          ariaBusy={isSsoLoading && isActive}
        />
      );
    });
  }, [
    providers,
    activeProvider,
    isSsoLoading,
    isSsoLocked,
    isFormLoading,
    rounded,
    handlers,
    getProviderLabels,
  ]);

  return <>{renderedButtons}</>;
};
